/**
 * rolly-lightbox.js — fullscreen image lightbox.
 * Swipe navigation, pinch-to-zoom, double-tap zoom, WCAG 2.1 AA.
 * Two groups: [data-lightbox-group="gallery"] and [data-lightbox-group="reverse"].
 * High-res source: data-full on the closest ancestor element.
 */
(function () {
  'use strict';

  var MAX_SCALE = 8;
  var ZOOM_ON_TAP = 2.5;
  var SWIPE_THRESHOLD = 0.25; // fraction of viewport width

  // Overlay state
  var _open = false;
  var _images = [];  // [{type:'image'|'video', full|src, poster, alt}]
  var _current = 0;
  var _trigger = null;

  // Zoom / pan state (current slide img)
  var _scale = 1;
  var _panX = 0;
  var _panY = 0;

  // DOM refs
  var _overlay, _track, _counter, _closeBtn, _prevBtn, _nextBtn;

  // Localised trigger aria-label (read from the overlay's data-open-label)
  var _openLabel = 'Open image fullscreen';
  // Localised aria-label for video slides (read from data-video-label)
  var _videoLabel = 'Video. Tap to play or pause.';

  // Pointer tracking
  var _ptrs = {};
  var _swipeStartX = 0;
  var _swipeActive = false;
  var _pinchStartDist = 0;
  var _pinchStartScale = 1;
  var _panStartX = 0;
  var _panStartY = 0;
  var _panOriginX = 0;
  var _panOriginY = 0;

  // Click vs drag discrimination and pointer type
  var _clickMoved = false;     // true when pointer moved >5px during current press
  var _lastPointerType = '';   // 'mouse' | 'touch' | 'pen'

  // Double-tap (touch only)
  var _lastTap = { t: 0, x: 0, y: 0 };

  // ---- Utilities ----

  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

  function ptrCount() { return Object.keys(_ptrs).length; }

  function pinchDist() {
    var pts = Object.values(_ptrs);
    if (pts.length < 2) return 0;
    var dx = pts[0].x - pts[1].x, dy = pts[0].y - pts[1].y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function currentImg() {
    if (!_track || !_track.children[_current]) return null;
    return _track.children[_current].querySelector('img');
  }

  function currentVideo() {
    if (!_track || !_track.children[_current]) return null;
    return _track.children[_current].querySelector('video');
  }

  function safePlay(video) {
    if (!video) return;
    video.muted = true; // never emit sound, even if the source has an audio track
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  // Play the current slide's video (muted), pause every other video.
  function syncVideoPlayback() {
    if (!_track) return;
    Array.prototype.forEach.call(_track.children, function (slide, i) {
      var v = slide.querySelector('video');
      if (!v) return;
      if (i === _current) safePlay(v);
      else v.pause();
    });
  }

  function pauseAllVideos() {
    if (!_track) return;
    Array.prototype.forEach.call(_track.querySelectorAll('video'), function (v) {
      v.pause();
    });
  }

  // ---- Transform ----

  function maxPan() {
    var img = currentImg();
    if (!img || _scale <= 1) return { x: 0, y: 0 };
    return { x: img.offsetWidth * (_scale - 1) / 2, y: img.offsetHeight * (_scale - 1) / 2 };
  }

  function applyTransform(noTransition) {
    var img = currentImg();
    if (!img) return;
    var m = maxPan();
    _panX = clamp(_panX, -m.x, m.x);
    _panY = clamp(_panY, -m.y, m.y);
    img.style.transition = (noTransition || prefersReducedMotion()) ? 'none' : 'transform 0.2s ease';
    img.style.transform = 'translate(' + _panX + 'px,' + _panY + 'px) scale(' + _scale + ')';
    img.classList.toggle('is-zoomed', _scale > 1);
  }

  function resetZoom(animate) {
    _scale = 1; _panX = 0; _panY = 0;
    var img = currentImg();
    if (img) {
      img.style.transition = (animate && !prefersReducedMotion()) ? 'transform 0.2s ease' : 'none';
      img.style.transform = '';
      img.classList.remove('is-zoomed', 'is-dragging');
    }
  }

  // Zoom towards a viewport point (double-tap + pinch share this)
  // noTransition=true during active pinch gesture for smooth continuous tracking
  function zoomToPoint(newScale, vx, vy, noTransition) {
    var img = currentImg();
    if (!img) return;
    var rect = img.getBoundingClientRect();
    // ox/oy: offset of the focal point from the image's visual centre.
    // getBoundingClientRect already includes _panX, so the correct pan update
    // is _panX + ox*(1-ratio), NOT _panX*ratio + ox*(1-ratio).
    var ox = vx - (rect.left + rect.width / 2);
    var oy = vy - (rect.top + rect.height / 2);
    var ratio = newScale / _scale;
    _panX = _panX + ox * (1 - ratio);
    _panY = _panY + oy * (1 - ratio);
    _scale = clamp(newScale, 1, MAX_SCALE);
    applyTransform(noTransition || false);
  }

  // ---- Slides ----

  function buildSlides() {
    if (!_track) return;
    _track.innerHTML = '';
    _images.forEach(function (item) {
      var slide = document.createElement('div');
      slide.className = 'rl-lightbox__slide';
      if (item.type === 'video') {
        var video = document.createElement('video');
        video.className = 'rl-lightbox__video';
        // Always muted, looping, inline — no native controls (no seek/volume).
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.preload = 'metadata';
        video.setAttribute('aria-label', item.alt ? item.alt + ' — ' + _videoLabel : _videoLabel);
        if (item.poster) video.poster = item.poster;
        // Don't force a ratio from the file dimensions — the real frame can differ
        // and that would letterbox the video. Let the element size to its own
        // intrinsic video frame; the vw/vh caps in CSS keep it from overflowing.
        var source = document.createElement('source');
        source.src = item.src || '';
        video.appendChild(source);
        slide.appendChild(video);
      } else {
        var img = document.createElement('img');
        img.src = item.full || '';
        img.alt = item.alt || '';
        img.draggable = false;
        slide.appendChild(img);
      }
      _track.appendChild(slide);
    });
  }

  function positionSlides(instant) {
    if (!_track) return;
    var noAnim = instant || prefersReducedMotion();
    Array.prototype.forEach.call(_track.children, function (slide, i) {
      slide.style.transition = noAnim ? 'none' : 'transform 0.3s ease';
      slide.style.transform = 'translateX(' + ((i - _current) * 100) + '%)';
    });
  }

  function updateUI() {
    if (_counter) _counter.textContent = (_current + 1) + ' / ' + _images.length;
    if (_prevBtn) _prevBtn.hidden = (_current === 0);
    if (_nextBtn) _nextBtn.hidden = (_current === _images.length - 1);
  }

  function goTo(index, instant) {
    _current = clamp(index, 0, _images.length - 1);
    positionSlides(instant);
    updateUI();
    resetZoom(false);
    syncVideoPlayback();
  }

  // ---- Open / close ----

  function openLightbox(images, startIndex, triggerEl) {
    if (!_overlay || !images || !images.length) return;
    _images = images;
    _trigger = triggerEl || null;
    buildSlides();
    _current = clamp(startIndex || 0, 0, images.length - 1);
    positionSlides(true);
    updateUI();
    resetZoom(false);
    document.body.style.overflow = 'hidden';
    document.body.classList.add('rl-lightbox-active');
    _overlay.setAttribute('aria-hidden', 'false');
    _overlay.classList.add('is-open');
    _open = true;
    // Let the inline gallery pause its background video while we own playback.
    document.dispatchEvent(new CustomEvent('rolly:lightbox-open'));
    syncVideoPlayback();
    requestAnimationFrame(function () { if (_closeBtn) _closeBtn.focus(); });
  }

  function closeLightbox() {
    if (!_overlay || !_open) return;
    pauseAllVideos();
    _overlay.classList.remove('is-open');
    _overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.body.classList.remove('rl-lightbox-active');
    _open = false;
    // Resume the inline gallery video if its slide is the one in view.
    document.dispatchEvent(new CustomEvent('rolly:lightbox-close'));
    var ret = _trigger;
    _trigger = null;
    if (ret && typeof ret.focus === 'function') ret.focus();
    setTimeout(function () { if (_track) _track.innerHTML = ''; },
      prefersReducedMotion() ? 0 : 280);
  }

  function prevSlide() { if (_current > 0) goTo(_current - 1); }
  function nextSlide() { if (_current < _images.length - 1) goTo(_current + 1); }

  // ---- Pointer events ----

  function onPointerDown(e) {
    if (!_open) return;
    _ptrs[e.pointerId] = { x: e.clientX, y: e.clientY };
    var count = ptrCount();
    if (count === 1) {
      _swipeStartX = e.clientX;
      _panStartX = e.clientX; _panStartY = e.clientY;
      _panOriginX = _panX; _panOriginY = _panY;
      _swipeActive = false;
      _clickMoved = false;
      _lastPointerType = e.pointerType || '';
      // No setPointerCapture: the lightbox is fullscreen so events stay within
      // it naturally. Capture would redirect click synthesis to the overlay,
      // breaking button hit-testing (e.target becomes the overlay, not the button).
    } else if (count === 2) {
      _swipeActive = false;
      _pinchStartDist = pinchDist();
      _pinchStartScale = _scale;
    }
  }

  function onPointerMove(e) {
    if (!_open) return;
    if (!_ptrs[e.pointerId]) return; // ignore hover/move without an active press
    _ptrs[e.pointerId] = { x: e.clientX, y: e.clientY };
    var count = ptrCount();

    if (count >= 2) {
      // Pinch zoom
      var dist = pinchDist();
      if (_pinchStartDist > 0) {
        var newScale = clamp(_pinchStartScale * (dist / _pinchStartDist), 1, MAX_SCALE);
        var pts = Object.values(_ptrs);
        var cx = (pts[0].x + pts[1].x) / 2;
        var cy = (pts[0].y + pts[1].y) / 2;
        zoomToPoint(newScale, cx, cy, true);
      }
      return;
    }

    if (count !== 1) return;
    var dx = e.clientX - _panStartX;
    var dy = e.clientY - _panStartY;

    // Mark as drag if moved >5px so onClick won't fire zoom at end of a drag
    if (!_clickMoved && Math.sqrt(dx * dx + dy * dy) > 5) _clickMoved = true;

    if (_scale > 1) {
      // Pan while zoomed — disable swipe
      _panX = _panOriginX + dx;
      _panY = _panOriginY + dy;
      var img = currentImg();
      if (img) img.classList.add('is-dragging');
      applyTransform(true);
    } else {
      // Horizontal swipe to navigate
      _swipeActive = true;
      if (_track) {
        var totalDx = e.clientX - _swipeStartX;
        Array.prototype.forEach.call(_track.children, function (slide, i) {
          slide.style.transition = 'none';
          slide.style.transform = 'translateX(calc(' + ((i - _current) * 100) + '% + ' + totalDx + 'px))';
        });
      }
    }
  }

  function onPointerUp(e) {
    if (!_open) return;
    var totalDx = e.clientX - _swipeStartX;
    delete _ptrs[e.pointerId];
    var img = currentImg();
    if (img) img.classList.remove('is-dragging');

    // 2 fingers → 1 finger: re-anchor the remaining pointer so the next
    // pan move calculates delta from current position, not the stale one.
    var remaining = Object.values(_ptrs);
    if (remaining.length === 1) {
      _panStartX = remaining[0].x;
      _panStartY = remaining[0].y;
      _panOriginX = _panX;
      _panOriginY = _panY;
      _swipeStartX = remaining[0].x;
      _swipeActive = false;
      return;
    }

    if (ptrCount() === 0 && _swipeActive && _scale <= 1) {
      var threshold = window.innerWidth * SWIPE_THRESHOLD;
      var before = _current;
      if (totalDx < -threshold) { nextSlide(); }
      else if (totalDx > threshold) { prevSlide(); }
      // Snap back whenever the index didn't actually change (sub-threshold
      // drag, or a past-threshold swipe that was a no-op at an edge). When
      // the index did change, goTo() already repositioned the slides.
      if (_current === before) positionSlides(false);
      _swipeActive = false;
    }
  }

  function onPointerCancel(e) {
    delete _ptrs[e.pointerId];
    if (_open && _swipeActive) { positionSlides(false); _swipeActive = false; }
    var img = currentImg();
    if (img) img.classList.remove('is-dragging');
  }

  // ---- Click (double-tap + backdrop) ----

  function onClick(e) {
    if (!_open) return;
    if (e.target.closest('.rl-lightbox__btn')) return;

    if (e.target.closest('.rl-lightbox__slide')) {
      if (!_clickMoved) {
        var vid = currentVideo();
        if (vid) {
          // Video slide: tap toggles play/pause (no zoom).
          if (vid.paused) safePlay(vid);
          else vid.pause();
          _clickMoved = false;
          return;
        }
        if (_lastPointerType === 'mouse') {
          // Desktop: single click toggles zoom
          _scale > 1 ? resetZoom(true) : zoomToPoint(ZOOM_ON_TAP, e.clientX, e.clientY);
        } else {
          // Touch: double-tap to zoom
          var now = Date.now();
          var isDouble = (now - _lastTap.t) < 300 &&
            Math.abs(e.clientX - _lastTap.x) < 40 &&
            Math.abs(e.clientY - _lastTap.y) < 40;
          _lastTap = { t: now, x: e.clientX, y: e.clientY };
          if (isDouble) {
            _scale > 1 ? resetZoom(true) : zoomToPoint(ZOOM_ON_TAP, e.clientX, e.clientY);
          }
        }
      }
      _clickMoved = false;
      return;
    }

    // Clicks on the track (black area around image) do nothing.
    // Only clicks that land directly on the overlay backdrop close it.
    if (e.target.closest('.rl-lightbox__track')) return;

    closeLightbox(); // true backdrop click
  }

  // ---- Wheel (trackpad pinch on desktop) ----

  function onWheel(e) {
    if (!_open) return;
    e.preventDefault();
    if (!e.ctrlKey) return; // ctrlKey=true for trackpad pinch on Mac/Windows
    var factor = Math.pow(1.08, -e.deltaY);
    var newScale = clamp(_scale * factor, 1, MAX_SCALE);
    zoomToPoint(newScale, e.clientX, e.clientY, true);
  }

  // ---- Keyboard ----

  function onKeydown(e) {
    if (!_open) return;
    if (e.key === 'Escape')      { e.preventDefault(); closeLightbox(); }
    else if (e.key === 'ArrowLeft')  { e.preventDefault(); prevSlide(); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); nextSlide(); }
    else if (e.key === 'Tab')    { trapFocus(e); }
  }

  function trapFocus(e) {
    if (!_overlay) return;
    var els = Array.prototype.slice.call(
      _overlay.querySelectorAll('button:not([hidden]):not(:disabled)')
    );
    if (!els.length) return;
    var first = els[0], last = els[els.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  // ---- Wire a group of images ----

  function wireGroup(groupSelector, triggerSelector) {
    var container = document.querySelector(groupSelector);
    if (!container) return;
    var triggers = container.querySelectorAll(triggerSelector);
    if (!triggers.length) return;

    // Build the media set from DOM; map each trigger element to its index.
    // A trigger is an <img> (image slide) or a <video> (video slide).
    var media = [];
    var map = new Map();
    Array.prototype.forEach.call(triggers, function (el) {
      var slide = el.closest('[data-media-type="video"]');
      if (el.tagName === 'VIDEO' || slide) {
        var src = (slide && slide.dataset.videoSrc) || el.currentSrc || '';
        if (!src) return;
        var caption = (slide && slide.dataset.caption) || el.getAttribute('aria-label') || '';
        map.set(el, media.length);
        media.push({
          type: 'video',
          src: src,
          poster: (slide && slide.dataset.poster) || el.poster || '',
          alt: caption
        });
      } else {
        var parent = el.closest('[data-full]');
        var full = (parent && parent.dataset.full) || el.src || '';
        if (!full) return;
        map.set(el, media.length);
        media.push({ type: 'image', full: full, alt: el.alt || '' });
      }
    });
    if (!media.length) return;

    Array.prototype.forEach.call(triggers, function (el) {
      var idx = map.get(el);
      if (idx === undefined) return;
      var label = media[idx].alt;
      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');
      el.setAttribute('aria-label', label ? label + ' — ' + _openLabel : _openLabel);
      el.classList.add('rl-lightbox-trigger');
      function doOpen() { openLightbox(media, idx, el); }
      el.addEventListener('click', doOpen);
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); doOpen(); }
      });
    });
  }

  // ---- Init ----

  function init() {
    _overlay  = document.getElementById('rl-lightbox');
    if (!_overlay) return;
    if (_overlay.dataset.openLabel) _openLabel = _overlay.dataset.openLabel;
    if (_overlay.dataset.videoLabel) _videoLabel = _overlay.dataset.videoLabel;
    _track    = document.getElementById('rl-lightbox-track');
    _counter  = document.getElementById('rl-lightbox-counter');
    _closeBtn = document.getElementById('rl-lightbox-close');
    _prevBtn  = document.getElementById('rl-lightbox-prev');
    _nextBtn  = document.getElementById('rl-lightbox-next');

    if (_closeBtn) _closeBtn.addEventListener('click', function(e) { e.stopPropagation(); closeLightbox(); });
    if (_prevBtn)  _prevBtn.addEventListener('click', function(e) { e.stopPropagation(); prevSlide(); });
    if (_nextBtn)  _nextBtn.addEventListener('click', function(e) { e.stopPropagation(); nextSlide(); });

    document.addEventListener('keydown', onKeydown);
    _overlay.addEventListener('wheel',        onWheel, { passive: false });
    _overlay.addEventListener('pointerdown',   onPointerDown);
    _overlay.addEventListener('pointermove',   onPointerMove);
    _overlay.addEventListener('pointerup',     onPointerUp);
    _overlay.addEventListener('pointercancel', onPointerCancel);
    _overlay.addEventListener('click',         onClick);

    wireGroup('[data-lightbox-group="gallery"]', '.pdp-gallery__slide img, .pdp-gallery__slide video');
    wireGroup('[data-lightbox-group="reverse"]', '.os-photo img');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
